/**
 * 分镜检查器 - 「帧序列」tab（image_sequence 项目模式专用）
 *
 * 与 ShotKeyframeTab 的区别：
 * - 这里同时管理「序列」和「候选池」两类帧
 * - 序列：已经按顺序加入的帧，激活时一并作为多参考图喂给视频模型
 * - 候选池：已生成但未加入序列的帧
 *
 * 操作：
 * - 候选 → 加入序列末尾（+ 按钮）
 * - 序列 → 移除（–）/ 上下移
 * - 任意帧 → 放大查看 / 删除（视频已激活时禁用修改）
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Plus,
  Trash2,
  ZoomIn,
  ChevronUp,
  ChevronDown,
  ImageOff,
  AlertCircle,
  CheckCircle2,
  Layers,
} from 'lucide-react';
import toast from '../../../../../components/Toast';
import {
  directorApi,
  DirectorApiError,
  type DirectorImageJob,
} from '../../../services/directorApi';
import { FrameLightbox, type FrameLightboxItem } from '../FrameLightbox';
import { BatchFrameGenerateDialog, clearBatchFrameDraft } from '../BatchFrameGenerateDialog';
import type {
  DirectorCharacter,
  DirectorProp,
  DirectorScene,
} from '../../../services/directorApi';
import type { DirectorAspectRatio } from '../../../utils/imageSize';
import { resolveBrowserMediaUrl } from '../../../../../utils/storageUrl';

interface ShotFrameSequenceTabProps {
  shotId: string;
  videoActive: boolean;
  projectAspectRatio: DirectorAspectRatio;
  /** 当前用户余额（批量生成弹窗内会显示+判定） */
  currentCredits: number;
  /** 当前分镜的 prompt（用作批量弹窗第 1 帧的默认文案） */
  shotPrompt: string;
  /** 项目可引用的资产（用于批量弹窗中各帧 prompt 的 @ 唤起） */
  projectCharacters: DirectorCharacter[];
  projectProps: DirectorProp[];
  projectScenes: DirectorScene[];
  /** 父组件触发"打开生成帧候选弹窗（单张）" */
  onRequestGenerate: () => void;
  /** 序列变更时通知外层（例如 contentBadge 等） */
  onSequenceChanged?: () => void;
}

interface SequenceItem {
  id: string;
  sortOrder: number;
  image: DirectorImageJob;
}

export function ShotFrameSequenceTab({
  shotId,
  videoActive,
  projectAspectRatio,
  currentCredits,
  shotPrompt,
  projectCharacters,
  projectProps,
  projectScenes,
  onRequestGenerate,
  onSequenceChanged,
}: ShotFrameSequenceTabProps) {
  const [sequence, setSequence] = useState<SequenceItem[]>([]);
  const [candidates, setCandidates] = useState<DirectorImageJob[]>([]);
  const [loading, setLoading] = useState(true);
  /** lightbox 当前打开的图片 ID（null 表示未打开） */
  const [previewId, setPreviewId] = useState<string | null>(null);
  /** 视频模型 maxReferenceImageCount；null 表示未配置或无法判断（不约束） */
  const [videoMax, setVideoMax] = useState<number | null>(null);

  // 批量生成弹窗
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchSubmitting, setBatchSubmitting] = useState(false);

  const reload = async () => {
    try {
      const result = await directorApi.listShotFrames(shotId, { limit: 50 });
      setSequence(result.sequence);
      setCandidates(result.candidates);
      setVideoMax(result.videoMaxReferenceImageCount);
    } catch (err) {
      // 静默：可能是 mode 不匹配或临时网络
      console.warn('[ShotFrameSequenceTab] reload failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shotId]);

  // 候选有进行中的图时，定时轮询
  useEffect(() => {
    const inFlight = candidates.some((c) =>
      ['pending', 'submitting', 'processing'].includes(c.status)
    );
    if (!inFlight) return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates]);

  const handleAdd = async (imageJobId: string) => {
    try {
      await directorApi.addFrameToSequence(shotId, imageJobId);
      await reload();
      onSequenceChanged?.();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '加入失败');
    }
  };

  const handleRemove = async (imageJobId: string) => {
    try {
      await directorApi.removeFrameFromSequence(shotId, imageJobId);
      await reload();
      onSequenceChanged?.();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '移除失败');
    }
  };

  const handleDelete = async (imageJobId: string) => {
    if (!window.confirm('确定要删除这张候选帧吗？删除后不可恢复。')) return;
    try {
      await directorApi.deleteFrame(imageJobId);
      await reload();
      onSequenceChanged?.();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '删除失败');
    }
  };

  const handleMove = async (idx: number, dir: -1 | 1) => {
    const next = [...sequence];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    const orderedImageJobIds = next.map((s) => s.image.id);
    // 乐观更新
    setSequence(next.map((s, i) => ({ ...s, sortOrder: i })));
    try {
      await directorApi.reorderSequence(shotId, orderedImageJobIds);
      onSequenceChanged?.();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '排序失败');
      reload();
    }
  };

  // Lightbox 可翻图列表：先序列后候选，仅纳入 completed + 有 resultUrl 的图。
  // 注意：必须在 early return 之前声明，否则会触发 React hooks 顺序异常。
  const lightboxSequenceItems = useMemo<FrameLightboxItem[]>(
    () =>
      sequence
        .filter((s) => s.image.status === 'completed' && !!s.image.resultUrl)
        .map((s, idx) => ({
          id: s.image.id,
          url: s.image.resultUrl!,
          label: `序列 ${idx + 1}`,
        })),
    [sequence]
  );
  const lightboxCandidateItems = useMemo<FrameLightboxItem[]>(
    () =>
      candidates
        .filter((c) => c.status === 'completed' && !!c.resultUrl)
        .map((c, idx) => ({
          id: c.id,
          url: c.resultUrl!,
          label: `候选 ${idx + 1}`,
        })),
    [candidates]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
      </div>
    );
  }

  // 视频模型参考图上限相关派生量
  const sequenceFull = videoMax !== null && videoMax > 0 && sequence.length >= videoMax;
  const noRefSupport = videoMax === 0;

  return (
    <div className="space-y-5">
      {videoActive && (
        <div className="flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertCircle className="h-4 w-4 shrink-0" />
          视频已激活，请先取消或重激活后再修改帧序列
        </div>
      )}

      {/* 视频模型参考图上限提示（仅 videoMax 已知时显示） */}
      {!videoActive && videoMax !== null && (
        <div
          className={`flex items-start gap-2 rounded-2xl px-3 py-2 text-[11px] leading-4 ${
            noRefSupport || sequenceFull
              ? 'border border-rose-200 bg-rose-50 text-rose-700'
              : 'border border-slate-200 bg-slate-50 text-slate-600'
          }`}
        >
          <AlertCircle
            className={`mt-0.5 h-3 w-3 shrink-0 ${
              noRefSupport || sequenceFull ? 'text-rose-500' : 'text-slate-400'
            }`}
          />
          <span className="flex-1">
            {noRefSupport ? (
              <>
                当前项目所选视频模型不支持参考图，无法使用帧序列模式生成视频。请前往项目设置切换视频模型。
              </>
            ) : (
              <>
                视频模型最多支持 <b>{videoMax}</b> 张参考图，序列已有
                <b> {sequence.length}</b> 张
                {sequenceFull ? <>，已达上限。</> : <>。</>}
              </>
            )}
          </span>
        </div>
      )}

      {/* 批量生成连贯帧入口 */}
      {!videoActive && (
        <button
          type="button"
          onClick={() => setBatchOpen(true)}
          disabled={noRefSupport || sequenceFull}
          className="group flex w-full items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 px-4 py-3 text-left transition hover:border-amber-300 hover:from-amber-100 hover:to-orange-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:from-slate-50 disabled:to-slate-50 disabled:hover:from-slate-50 disabled:hover:to-slate-50"
        >
          <div className="flex items-start gap-3">
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white shadow-md ${
                noRefSupport || sequenceFull
                  ? 'bg-slate-300'
                  : 'bg-gradient-to-br from-amber-500 to-orange-400'
              }`}
            >
              <Layers className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p
                className={`text-sm font-semibold ${
                  noRefSupport || sequenceFull ? 'text-slate-500' : 'text-amber-900'
                }`}
              >
                批量生成连贯帧序列
              </p>
              <p
                className={`mt-0.5 text-[11px] leading-4 ${
                  noRefSupport || sequenceFull ? 'text-slate-400' : 'text-amber-700'
                }`}
              >
                {sequenceFull
                  ? `序列已达视频模型上限（${videoMax} 张），请先移除一些帧`
                  : noRefSupport
                    ? '当前视频模型不支持参考图，无法批量生成'
                    : '以一张参考图为锚定，一次生成多帧（保持主体一致）'}
              </p>
            </div>
          </div>
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold text-white opacity-90 transition group-hover:opacity-100 ${
              noRefSupport || sequenceFull ? 'bg-slate-300' : 'bg-amber-500'
            }`}
          >
            开始
          </span>
        </button>
      )}

      {/* 序列区 */}
      <section>
        <h4 className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
          <Layers className="h-3 w-3" /> 帧序列（{sequence.length}）
          <span className="ml-1 normal-case tracking-normal text-[10px] font-normal text-slate-400">
            按顺序作为视频模型多参考图
          </span>
        </h4>
        {sequence.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white/40 py-8 text-xs text-slate-500">
            <Layers className="h-6 w-6 text-slate-300" />
            还没有帧。先在下方候选区生成几张图，再加入序列
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {sequence.map((s, idx) => {
              const canZoom = s.image.status === 'completed' && !!s.image.resultUrl;
              return (
                <div
                  key={s.id}
                  className="group relative aspect-square overflow-hidden rounded-2xl bg-slate-50 ring-2 ring-amber-300"
                >
                  {s.image.status === 'completed' && s.image.resultUrl ? (
                    <img
                      src={resolveBrowserMediaUrl(s.image.resultUrl)}
                      alt=""
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  ) : s.image.status === 'failed' ? (
                    <div className="flex h-full w-full items-center justify-center bg-rose-50 text-xs text-rose-500">
                      失败
                    </div>
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-slate-100">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                  )}

                  {/* 序号徽标 */}
                  <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-md">
                    <CheckCircle2 className="h-3 w-3" />
                    {idx + 1}
                  </span>

                  {/* 操作蒙层 */}
                  {!videoActive && (
                    <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-slate-950/45 opacity-0 transition group-hover:opacity-100">
                      {canZoom && (
                        <button
                          type="button"
                          onClick={() => setPreviewId(s.image.id)}
                          className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-amber-50"
                          title="放大查看"
                        >
                          <ZoomIn className="h-4 w-4 text-slate-700" />
                        </button>
                      )}
                      {idx > 0 && (
                        <button
                          type="button"
                          onClick={() => handleMove(idx, -1)}
                          className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-amber-50"
                          title="上移"
                        >
                          <ChevronUp className="h-4 w-4 text-slate-700" />
                        </button>
                      )}
                      {idx < sequence.length - 1 && (
                        <button
                          type="button"
                          onClick={() => handleMove(idx, 1)}
                          className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-amber-50"
                          title="下移"
                        >
                          <ChevronDown className="h-4 w-4 text-slate-700" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemove(s.image.id)}
                        className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-slate-100"
                        title="从序列移除"
                      >
                        <Trash2 className="h-4 w-4 text-slate-600" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 候选区 */}
      <section>
        <h4 className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
          候选帧（{candidates.length}）
          <span className="ml-1 normal-case tracking-normal text-[10px] font-normal text-slate-400">
            已生成但未加入序列
          </span>
        </h4>

        <div className="grid grid-cols-3 gap-2">
          {candidates.map((c) => {
            const canZoom = c.status === 'completed' && !!c.resultUrl;
            return (
              <div
                key={c.id}
                className="group relative aspect-square overflow-hidden rounded-2xl bg-slate-50 ring-1 ring-slate-200"
              >
                {c.status === 'completed' && c.resultUrl ? (
                  <img
                    src={resolveBrowserMediaUrl(c.resultUrl)}
                    alt=""
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                ) : c.status === 'failed' ? (
                  <div className="flex h-full w-full items-center justify-center bg-rose-50 text-xs text-rose-500">
                    失败
                  </div>
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-slate-100">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                  </div>
                )}

                {c.status === 'completed' && (
                  <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-slate-950/45 opacity-0 transition group-hover:opacity-100">
                    {canZoom && (
                      <button
                        type="button"
                        onClick={() => setPreviewId(c.id)}
                        className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-amber-50"
                        title="放大查看"
                      >
                        <ZoomIn className="h-4 w-4 text-slate-700" />
                      </button>
                    )}
                    {!videoActive && (
                      <button
                        type="button"
                        onClick={() => handleAdd(c.id)}
                        disabled={sequenceFull}
                        className="rounded-full bg-amber-500 p-1.5 shadow-md transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-slate-300"
                        title={sequenceFull ? `序列已达视频模型上限（${videoMax} 张）` : '加入序列'}
                      >
                        <Plus className="h-4 w-4 text-white" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(c.id)}
                      className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-rose-50"
                      title="删除候选"
                    >
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* 生成新候选 */}
          <button
            type="button"
            onClick={onRequestGenerate}
            disabled={videoActive}
            className="flex aspect-square flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-slate-200 bg-white text-slate-400 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="h-5 w-5" />
            <span className="text-[10px]">生成候选</span>
          </button>
        </div>

        {candidates.length === 0 && (
          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400">
            <ImageOff className="h-3 w-3" />
            还没有候选帧，点上方"生成候选"开始
          </p>
        )}
      </section>

      {(() => {
        // 把"序列 + 候选"按当前可见顺序拼成一个统一的可翻列表。
        // 仅纳入 status=completed 且 resultUrl 非空的图（pending / failed 不可放大）。
        const lightboxItems: FrameLightboxItem[] = [
          ...lightboxSequenceItems,
          ...lightboxCandidateItems,
        ];
        const currentIndex = previewId
          ? lightboxItems.findIndex((it) => it.id === previewId)
          : -1;
        if (currentIndex < 0) return null;
        return (
          <FrameLightbox
            items={lightboxItems}
            currentIndex={currentIndex}
            onIndexChange={(i) => setPreviewId(lightboxItems[i]?.id ?? null)}
            onClose={() => setPreviewId(null)}
            buildActions={(item) => {
              const seqIdx = sequence.findIndex((s) => s.image.id === item.id);
              const isInSequence = seqIdx >= 0;
              if (isInSequence) {
                // 序列帧的操作
                return [
                  {
                    key: 'move-up',
                    icon: <ChevronUp className="h-4 w-4" />,
                    label: '上移',
                    onClick: () => handleMove(seqIdx, -1),
                    show: !videoActive && seqIdx > 0,
                  },
                  {
                    key: 'move-down',
                    icon: <ChevronDown className="h-4 w-4" />,
                    label: '下移',
                    onClick: () => handleMove(seqIdx, 1),
                    show: !videoActive && seqIdx < sequence.length - 1,
                  },
                  {
                    key: 'remove',
                    icon: <Trash2 className="h-4 w-4" />,
                    label: '从序列移除',
                    onClick: () => handleRemove(item.id),
                    variant: 'danger',
                    show: !videoActive,
                  },
                ];
              }
              // 候选帧的操作
              return [
                {
                  key: 'add',
                  icon: <Plus className="h-4 w-4" />,
                  label: sequenceFull
                    ? `已达上限（${videoMax}）`
                    : '加入序列',
                  onClick: () => handleAdd(item.id),
                  variant: 'primary',
                  disabled: sequenceFull,
                  show: !videoActive,
                },
                {
                  key: 'delete',
                  icon: <Trash2 className="h-4 w-4" />,
                  label: '删除',
                  onClick: () => handleDelete(item.id),
                  variant: 'danger',
                },
              ];
            }}
          />
        );
      })()}

      {/* 批量生成连贯帧弹窗 */}
      {batchOpen && (
        <BatchFrameGenerateDialog
          open
          shotId={shotId}
          projectAspectRatio={projectAspectRatio}
          currentCredits={currentCredits}
          defaultPrompt={shotPrompt}
          projectCharacters={projectCharacters}
          projectProps={projectProps}
          projectScenes={projectScenes}
          submitting={batchSubmitting}
          anchors={[
            ...sequence
              .filter((s) => s.image.status === 'completed' && s.image.resultUrl)
              .map((s, idx) => ({
                imageJobId: s.image.id,
                url: s.image.resultUrl!,
                label: `序列 ${idx + 1}`,
              })),
            ...candidates
              .filter((c) => c.status === 'completed' && c.resultUrl)
              .map((c, idx) => ({
                imageJobId: c.id,
                url: c.resultUrl!,
                label: `候选 ${idx + 1}`,
              })),
          ]}
          onCancel={() => setBatchOpen(false)}
          onVideoGuidanceGenerated={async (videoGuidance) => {
            // AI 顺带生成的「视频指导」（动作 / 节奏 / 过渡）写回 shot.prompt。
            // 写完后通知上层 reload，让 ShotInspector 的「视频指导」textarea 反映新文本。
            await directorApi.updateShot(shotId, { prompt: videoGuidance });
            onSequenceChanged?.();
          }}
          onConfirm={async (input) => {
            setBatchSubmitting(true);
            try {
              const result = await directorApi.batchGenerateFrames(shotId, input);
              if (result.fellBackToText2Img) {
                toast.success(
                  `已提交 ${result.jobIds.length} 帧（消耗 ${result.creditsUsed} 积分，注意：模型不支持 img2img，已降级为 text2img）`
                );
              } else {
                toast.success(
                  `已提交 ${result.jobIds.length} 帧（消耗 ${result.creditsUsed} 积分）`
                );
              }
              // 提交成功 → 清除 localStorage 草稿（取消时保留草稿，留给用户下次继续编辑）
              clearBatchFrameDraft(shotId);
              setBatchOpen(false);
              await reload();
              onSequenceChanged?.();
            } catch (err) {
              toast.error(
                err instanceof DirectorApiError ? err.message : '批量生成失败'
              );
            } finally {
              setBatchSubmitting(false);
            }
          }}
        />
      )}
    </div>
  );
}
